import { useQuery } from '@tanstack/react-query'
import { useFormContext } from 'react-hook-form'

import { cn, toISO8601 } from '@fortem-monorepo/js-utilities'
import { useApi } from '@fortem-monorepo/react-spa'
import { Checkbox } from '@fortem-monorepo/react-ui'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { FormError } from '@fortem-monorepo/react-ui'
import { FormField, FormItem, FormLabel } from '@fortem-monorepo/react-ui'
import { Input } from '@fortem-monorepo/react-ui'
import { MultiSelectCreate } from '@fortem-monorepo/react-ui'
import { Switch } from '@fortem-monorepo/react-ui'

import { SchemaType } from './RunMarketDataJobsForm.schema'

export interface RunMarketDataJobsFormFieldsProps {}

export const RunMarketDataJobsFormFields: React.FC<RunMarketDataJobsFormFieldsProps> = ({}) => {
  const { control, formState, setValue } = useFormContext<SchemaType>()

  const api = useApi()

  const jobsQuery = useQuery({
    queryKey: ['marketdata', 'jobs'],
    queryFn: () => api.marketdata.getJobs(),
  })

  return (
    <div className="flex flex-col gap-4">
      <FormField
        name="value_date"
        control={control}
        render={({ field: { value } }) => {
          return (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Value Date</FormLabel>
                <FormError />
              </div>
              <DatePicker
                buttonDisabled={formState.isSubmitting}
                selected={value ? new Date(value) : undefined}
                onSelect={(date) =>
                  date && setValue('value_date', toISO8601(date), { shouldValidate: true })
                }
              />
            </FormItem>
          )
        }}
      />
      <FormField
        name="chosen_jobs"
        control={control}
        render={({ field: { value } }) => {
          const jobs = jobsQuery.data ?? ({} as Record<string, string>)

          return (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Jobs</FormLabel>
                <FormError />
              </div>
              <div className="space-y-2 rounded-md border p-3 md:grid-cols-2">
                {jobsQuery.isLoading && (
                  <span className="text-muted-foreground text-sm">Loading jobs...</span>
                )}
                {!jobsQuery.isLoading && Object.entries(jobs).length === 0 && (
                  <span className="text-muted-foreground text-sm">No jobs available</span>
                )}
                {Object.entries(jobs).map(([jobKey, jobLabel]) => {
                  const checked = value.includes(jobKey)

                  return (
                    <label key={jobKey} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        disabled={formState.isSubmitting || jobsQuery.isLoading}
                        checked={checked}
                        onCheckedChange={() =>
                          setValue('chosen_jobs', toggleSelectedValue(value, jobKey), {
                            shouldValidate: true,
                          })
                        }
                      />
                      <span
                        className={cn({
                          'text-green-700': checked,
                        })}
                      >
                        {jobLabel}
                      </span>
                    </label>
                  )
                })}
              </div>
            </FormItem>
          )
        }}
      />
      <FormField
        name="only_tickers"
        control={control}
        render={({ field: { value } }) => {
          return (
            <FormItem>
              <div className="flex items-center gap-2">
                <FormLabel>Only Tickers</FormLabel>
                <FormError />
              </div>
              <MultiSelectCreate
                disabled={formState.isSubmitting}
                items={[]}
                value={value}
                onValueChange={(value) => setValue('only_tickers', value, { shouldValidate: true })}
                pluralPhrase="Tickers"
                singularPhrase="Ticker"
              />
            </FormItem>
          )
        }}
      />
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          name="ticker_cooldown_minutes"
          control={control}
          render={({ field: { value } }) => {
            return (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Ticker Cooldown Minutes</FormLabel>
                  <FormError />
                </div>
                <Input
                  disabled={formState.isSubmitting}
                  min={0}
                  type="number"
                  value={value ?? ''}
                  onChange={(event) =>
                    setValue('ticker_cooldown_minutes', parseOptionalNumber(event.target.value), {
                      shouldValidate: true,
                    })
                  }
                />
              </FormItem>
            )
          }}
        />
        <FormField
          name="job_cooldown_minutes"
          control={control}
          render={({ field: { value } }) => {
            return (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Job Cooldown Minutes</FormLabel>
                  <FormError />
                </div>
                <Input
                  disabled={formState.isSubmitting}
                  min={0}
                  type="number"
                  value={value ?? ''}
                  onChange={(event) =>
                    setValue('job_cooldown_minutes', parseOptionalNumber(event.target.value), {
                      shouldValidate: true,
                    })
                  }
                />
              </FormItem>
            )
          }}
        />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <FormField
          name="force_historical"
          control={control}
          render={({ field: { value } }) => {
            return (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Force Historical</FormLabel>
                  <FormError />
                </div>
                <Switch
                  disabled={formState.isSubmitting}
                  checked={value}
                  onCheckedChange={(value) =>
                    setValue('force_historical', value, { shouldValidate: true })
                  }
                />
              </FormItem>
            )
          }}
        />
        <FormField
          name="skip_closed_european"
          control={control}
          render={({ field: { value } }) => {
            return (
              <FormItem>
                <div className="flex items-center gap-2">
                  <FormLabel>Skip Closed European</FormLabel>
                  <FormError />
                </div>
                <Switch
                  disabled={formState.isSubmitting}
                  checked={value}
                  onCheckedChange={(value) =>
                    setValue('skip_closed_european', value, { shouldValidate: true })
                  }
                />
              </FormItem>
            )
          }}
        />
      </div>
    </div>
  )
}

const parseOptionalNumber = (value: string) => {
  return value === '' ? undefined : Number(value)
}

const toggleSelectedValue = (selectedValues: string[], value: string) => {
  return selectedValues.includes(value)
    ? selectedValues.filter((selectedValue) => selectedValue !== value)
    : [...selectedValues, value]
}
